import { useMemo, useState } from "react";
import {
  addDays,
  addMonths,
  formatLongDate,
  isSameDay,
  parseISODate,
  startOfDay,
  toISODate,
} from "@/lib/date";
import type { BookedRange } from "@/hooks/useVehicle";

interface SelectedRange {
  start: string | null;
  end: string | null;
}

interface AvailabilityCalendarProps {
  bookedRanges: BookedRange[];
  range: SelectedRange;
  onChange: (next: SelectedRange) => void;
}

const WEEKDAYS = ["Su", "Mo", "Tu", "We", "Th", "Fr", "Sa"];

export default function AvailabilityCalendar({ bookedRanges, range, onChange }: AvailabilityCalendarProps) {
  const today = startOfDay(new Date());
  const [cursor, setCursor] = useState(() => new Date(today.getFullYear(), today.getMonth(), 1));

  const isBooked = (date: Date) =>
    bookedRanges.some((booked) => {
      const start = parseISODate(booked.start);
      const end = parseISODate(booked.end);
      return date >= startOfDay(start) && date < startOfDay(end);
    });

  const isDisabled = (date: Date) => date < today || isBooked(date);

  const crossesBlocked = (start: Date, end: Date) => {
    let cursorDay = startOfDay(start);
    const last = startOfDay(addDays(end, -1));
    while (cursorDay <= last) {
      if (isDisabled(cursorDay)) return true;
      cursorDay = addDays(cursorDay, 1);
    }
    return false;
  };

  const handleSelect = (date: Date) => {
    if (isDisabled(date)) return;
    const iso = toISODate(date);
    if (!range.start || (range.start && range.end)) {
      onChange({ start: iso, end: null });
      return;
    }
    if (iso === range.start) {
      onChange({ start: null, end: null });
      return;
    }
    if (date < parseISODate(range.start)) {
      onChange({ start: iso, end: null });
      return;
    }
    if (crossesBlocked(parseISODate(range.start), date)) {
      onChange({ start: iso, end: null });
      return;
    }
    onChange({ start: range.start, end: iso });
  };

  const cells = useMemo(() => {
    const first = new Date(cursor.getFullYear(), cursor.getMonth(), 1);
    const gridStart = addDays(first, -first.getDay());
    return Array.from({ length: 42 }, (_, index) => addDays(gridStart, index));
  }, [cursor]);

  const monthLabel = cursor.toLocaleDateString("en-KE", { month: "long", year: "numeric" });
  const canGoBack = new Date(cursor.getFullYear(), cursor.getMonth(), 1) > new Date(today.getFullYear(), today.getMonth(), 1);

  const startDate = range.start ? parseISODate(range.start) : null;
  const endDate = range.end ? parseISODate(range.end) : null;

  return (
    <div className="rounded-2xl surface-card p-5">
      <div className="flex items-center justify-between">
        <button
          type="button"
          disabled={!canGoBack}
          onClick={() => setCursor((prev) => addMonths(prev, -1))}
          aria-label="Previous month"
          className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-lg text-foreground-900 transition-colors hover:border-primary-500/60 disabled:cursor-not-allowed disabled:opacity-40"
        >
          <i className="ri-arrow-left-s-line"></i>
        </button>
        <p className="font-heading text-base font-semibold text-foreground-950">{monthLabel}</p>
        <button
          type="button"
          onClick={() => setCursor((prev) => addMonths(prev, 1))}
          aria-label="Next month"
          className="flex h-9 w-9 items-center justify-center rounded-md border border-background-200 text-lg text-foreground-900 transition-colors hover:border-primary-500/60"
        >
          <i className="ri-arrow-right-s-line"></i>
        </button>
      </div>

      <div className="mt-5 grid grid-cols-7 gap-1 text-center">
        {WEEKDAYS.map((day) => (
          <span key={day} className="py-1 text-xs font-medium text-foreground-500">
            {day}
          </span>
        ))}
        {cells.map((date) => {
          const inMonth = date.getMonth() === cursor.getMonth();
          const disabled = isDisabled(date);
          const isStart = startDate ? isSameDay(date, startDate) : false;
          const isEnd = endDate ? isSameDay(date, endDate) : false;
          const inRange =
            startDate && endDate ? date > startDate && date < endDate : false;
          const selected = isStart || isEnd;

          let classes = "text-foreground-800 hover:bg-background-200/60";
          if (!inMonth) classes = "text-foreground-400/40";
          if (inRange) classes = "bg-primary-500/15 text-foreground-950";
          if (selected) classes = "bg-primary-500 text-background-50 font-semibold";
          if (disabled) classes = "text-foreground-400/40 line-through cursor-not-allowed hover:bg-transparent";

          return (
            <button
              key={toISODate(date)}
              type="button"
              disabled={disabled}
              onClick={() => handleSelect(date)}
              className={`relative flex h-9 items-center justify-center rounded-md text-sm transition-colors ${classes}`}
            >
              {date.getDate()}
            </button>
          );
        })}
      </div>

      <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 border-t border-background-200/70 pt-4 text-xs text-foreground-500">
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm border border-background-300 bg-background-50"></span> Available
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-primary-500"></span> Selected
        </span>
        <span className="inline-flex items-center gap-1.5">
          <span className="h-3 w-3 rounded-sm bg-background-300/50"></span> Booked
        </span>
      </div>

      <div className="mt-4 rounded-lg bg-background-200/40 px-4 py-3 text-sm">
        {range.start && range.end ? (
          <p className="flex items-center gap-2 text-foreground-800">
            <i className="ri-calendar-check-line text-primary-500"></i>
            {formatLongDate(range.start)} → {formatLongDate(range.end)}
          </p>
        ) : range.start ? (
          <p className="flex items-center gap-2 text-foreground-600">
            <i className="ri-calendar-line text-primary-500"></i>
            Pick-up {formatLongDate(range.start)} — now choose a return date
          </p>
        ) : (
          <p className="flex items-center gap-2 text-foreground-600">
            <i className="ri-calendar-line text-primary-500"></i>
            Select your pick-up date to start
          </p>
        )}
      </div>
    </div>
  );
}